import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@shadcn/components/ui/chart';
import { useGetUserCountsByDay } from '@shadcn/hooks/use-user-counts';
import { Alert } from '~/components/ui/Alert';
import { Skeleton } from '~/components/ui/Skeleton';
import { themeIsDark } from '~/lib/stores/theme';

const chartConfig = {
  users: {
    label: 'New Users',
    color: '#0451a5',
  },
} satisfies ChartConfig;

export function UsersGrowthChart({ timeRange }: { timeRange: number }) {
  const { data: chartData, isLoading, isError } = useGetUserCountsByDay(timeRange);

  return (
    <div>
      {isLoading && <Skeleton className="h-[250px] w-full rounded-none" />}
      {isError && (
        <Alert type="danger" title="Error loading chart data">
          There was a problem loading your chart data.
        </Alert>
      )}
      {chartData && !isLoading && (
        <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
          <BarChart
            accessibilityLayer
            data={chartData}
            margin={{
              left: -24,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} stroke={themeIsDark() ? '#ffffff15' : '#00000015'} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                });
              }}
            />
            <YAxis
              tick={{ stroke: '#828282', strokeWidth: 0.25 }}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickCount={5}
              allowDecimals={false}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  className="w-[150px]"
                  labelFormatter={(value) => {
                    return new Date(value).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    });
                  }}
                  indicator="dot"
                />
              }
            />
            <Bar dataKey="users" fill="var(--color-users)" />
          </BarChart>
        </ChartContainer>
      )}
    </div>
  );
}
